import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { io, Socket } from 'socket.io-client';
import { env } from '@/config/env';
import {
  useGeolocationStore,
  GeolocationService,
  NEARBY_RADIUS_M,
  bboxAround,
  type LatLon,
} from '@/modules/geolocation';
import { IndexedDBStore } from '@/modules/persistence';
import { useAlertStore } from '@/modules/alerts';
import { useSettingsStore } from '@/modules/settings';
import { useDetectionStore } from '@/modules/detection';
import { visitorLog } from '@/modules/visitor/visitorLog';
import {
  isTelegramLocationSupported,
  getTelegramLocation,
  updatePushRegion,
  isTelegramCloudStorageAvailable,
  tgCloudSetItem,
  tgCloudGetItem,
  getDeviceId,
  getUserId,
} from '@/modules/platform';
import { trace, installGlobalErrorCapture } from '@/utils/onScreenLog';
import type { Location } from '@/types';
import type { ServerToClientEvents, ClientToServerEvents } from '@/types/geolocation';
import { useLiveDetection } from './useLiveDetection';
import { pickSensitivity } from './alarmSensitivity';

/**
 * Императивные операции карты, которые нужны логике радара. Страница передаёт
 * сюда обёртки над ref на RadarMapView — сама логика про Leaflet не знает.
 */
export interface RadarMapOps {
  init: (center: LatLon, markUser?: boolean) => void;
  setCenter: (center: LatLon, keepZoom?: boolean) => void;
  updateMarkers: () => void;
  updateQuadrants: () => void;
  updateTrajectories: () => void;
  updateTracks: () => void;
  highlightExplosionQuadrant: (row: number, col: number, count: number) => void;
  zoomForPick: (center: LatLon | null) => void;
}

/**
 * Вся не-картографическая логика страницы радара: локация (Telegram/GPS/
 * вручную/кэш), WebSocket-подписка на регион, presence онлайн-мониторов,
 * arm/reconcile мониторинга, lifecycle. Вызывается один раз из setup
 * LiveRadarPage; регистрирует свои onMounted/onUnmounted.
 */
export function useRadarLogic(map: RadarMapOps) {
  const store = useGeolocationStore();
  const alertStore = useAlertStore();
  const settings = useSettingsStore();
  const detectionStore = useDetectionStore();
  const cache = new IndexedDBStore();
  const gps = new GeolocationService();

  const locating = ref(true);

  const gpsError = ref<string | null>(null);
  const syncing = ref(false);

  /** Latest device position with accuracy — attached to detections sent to the backend. */
  const lastLocation = ref<Location | null>(null);

  /** Where the current centre came from, and its reported accuracy (metres). */
  type LocationSource = 'telegram' | 'gps' | 'manual' | 'cache';
  const locationSource = ref<LocationSource | null>(null);
  const accuracy = ref<number | null>(null);
  /** When true, the next tap on the map sets the user position manually. */
  const manualMode = ref(false);
  /**
   * Mobile only: remembers whether the settings panel was open before manual placement collapsed
   * it, so we can restore it after the point is committed. Stays false on desktop.
   */
  let toolsBeforePick = false;
  /** Coordinate input fields for typing the position by hand (alternative to tapping the map). */
  const manualLat = ref('');
  const manualLon = ref('');
  const manualError = ref<string | null>(null);
  /** Briefly true right after a manual point is committed — drives the "✓ applied" confirmation. */
  const manualApplied = ref(false);
  let manualAppliedTimer: ReturnType<typeof setTimeout> | null = null;
  /** Accuracy assumed for a hand-placed point (a deliberate map tap is ~block-level precise). */
  const MANUAL_ACCURACY_M = 50;
  /** Telegram Mini App exposes a native LocationManager — prefer it over the browser API. */
  const telegramLocation = isTelegramLocationSupported();

  /**
   * By default we read the position ONCE and remember it — keeping GPS on continuously drains the
   * phone battery. "Live tracking" is an opt-in option (persisted) for users who move around and
   * want the radius to follow them.
   */
  const TRACKING_KEY = 'pushka:liveTracking';
  const liveTracking = ref(loadTrackingPref());

  function loadTrackingPref(): boolean {
    try {
      return localStorage.getItem(TRACKING_KEY) === '1';
    } catch {
      return false;
    }
  }
  function saveTrackingPref(on: boolean): void {
    try {
      localStorage.setItem(TRACKING_KEY, on ? '1' : '0');
    } catch {
      /* ignore */
    }
  }

  /**
   * Persist the location source ('manual' | 'gps' | 'telegram' | 'cache') so that on reload
   * we know whether to request GPS or skip it.  When inside Telegram, the value is also
   * written to CloudStorage so it follows the user across devices.
   */
  const LOC_SOURCE_KEY = 'pushka:locationSource';
  let cloudSourceHydrated = false;

  function loadLocationSource(): LocationSource | null {
    try {
      const raw = localStorage.getItem(LOC_SOURCE_KEY);
      if (raw === 'telegram' || raw === 'gps' || raw === 'manual' || raw === 'cache') return raw;
    } catch {
      /* ignore */
    }
    return null;
  }

  async function hydrateLocationSourceFromCloud(): Promise<void> {
    if (!isTelegramCloudStorageAvailable() || cloudSourceHydrated) return;
    cloudSourceHydrated = true;
    const cloud = await tgCloudGetItem(LOC_SOURCE_KEY);
    if (
      cloud === 'telegram' || cloud === 'gps' || cloud === 'manual' || cloud === 'cache'
    ) {
      locationSource.value = cloud;
      try { localStorage.setItem(LOC_SOURCE_KEY, cloud); } catch { /* ignore */ }
    }
  }

  function saveLocationSource(source: LocationSource): void {
    try {
      localStorage.setItem(LOC_SOURCE_KEY, source);
    } catch {
      /* ignore */
    }
    if (isTelegramCloudStorageAvailable()) {
      void tgCloudSetItem(LOC_SOURCE_KEY, source);
    }
  }

  const detection = useLiveDetection({ getLocation: () => lastLocation.value });

  /**
   * Remember whether the user had monitoring (recording) on, so a reload resumes it.
   * Auto-resume needs an already-granted mic permission; if the browser still
   * requires a user gesture, start() throws and we silently stay off.
   */
  const MONITOR_KEY = 'pushka:liveMonitoring';
  function saveMonitorPref(on: boolean): void {
    try {
      localStorage.setItem(MONITOR_KEY, on ? '1' : '0');
    } catch {
      /* ignore */
    }
  }
  function loadMonitorPref(): boolean {
    try {
      return localStorage.getItem(MONITOR_KEY) === '1';
    } catch {
      return false;
    }
  }

  /**
   * The persisted toggle is the user's *intent* ("armed"), not the mic state itself: in the
   * alertOnly power mode an armed detector stays dormant until an air-raid alert starts nearby.
   * A manual stop disarms, so an alert never force-starts a mic the user explicitly turned off.
   */
  const armed = ref(loadMonitorPref());

  /** Whether the mic + cascade should actually be running right now. */
  const shouldCapture = computed(
    () => armed.value && (settings.powerMode !== 'alertOnly' || alertStore.alertActive !== false),
  );

  /** Armed but suppressed by alertOnly (no alert nearby) — the chip shows a "waiting" state. */
  const waitingForAlert = computed(() => armed.value && !shouldCapture.value);

  /**
   * Single place that brings the actual mic/pipeline state in line with `shouldCapture`.
   * Called from the user toggle, the alert-state watcher and the mount-time auto-resume.
   */
  let reconciling = false;
  async function reconcileMonitoring(): Promise<void> {
    if (reconciling) return;
    reconciling = true;
    try {
      // The alert state can flip again while start() is awaiting (model load takes seconds) —
      // loop until the actual state matches the desired one. Bounded for paranoia.
      for (let i = 0; i < 3 && shouldCapture.value !== detection.monitoring.value; i++) {
        if (shouldCapture.value) {
          try {
            await detection.start();
            emitStartMonitor();
            // Coming back from alertOnly suppression: GPS watch was paused too — restore it.
            if (liveTracking.value && settings.powerMode === 'alertOnly') startWatching();
          } catch (e) {
            // Browser demanded a user gesture / mic denied — disarm so the UI stays honest;
            // the existing monitor-error block surfaces the message, a tap retries.
            console.warn('[LiveRadar] failed to start monitoring:', e);
            armed.value = false;
            saveMonitorPref(false);
          }
        } else {
          detection.stop();
          emitStopMonitor();
          // alertOnly battery win: while dormant, release the GPS watch as well.
          if (waitingForAlert.value) stopWatching();
        }
      }
    } finally {
      reconciling = false;
    }
  }

  watch(shouldCapture, () => void reconcileMonitoring());

  /** Monitoring toggle: arm/disarm; the reconciler starts or stops the actual capture. */
  async function toggleMonitoring(): Promise<void> {
    armed.value = !armed.value;
    saveMonitorPref(armed.value);
    await reconcileMonitoring();
  }

  /**
   * Alarm-driven sensitivity (Block 4) + adaptive power mode.
   *
   * Three levels, evaluated in priority order (wartime > powersave > normal). The pure rule lives
   * in pickSensitivity() so it is unit-tested in isolation; this watcher just applies its output
   * to the live pipeline. See alarmSensitivity.ts for the full rationale and reproducibility note.
   *
   *   1. WARTIME   — alert active nearby → wartime Stage-1 threshold + `sensitive` energy preset
   *                  (recall prioritised over battery during an active threat).
   *   2. POWERSAVE — adaptive mode, no alert → raised Stage-1 threshold, `balanced` preset.
   *   3. NORMAL    — unknown alert / non-adaptive → default thresholds, `balanced` preset.
   */
  watch(
    () => [settings.powerMode, alertStore.alertActive] as const,
    () => {
      const decision = pickSensitivity(settings.powerMode, alertStore.alertActive);
      detection.setStage1Threshold(decision.stage1Threshold);
      detection.setEnergyPreset(decision.energyPreset);
    },
    { immediate: true },
  );

  /**
   * Secondary controls (spectrogram, self-diagnostic, location pickers, live tracking) are tucked
   * into a collapsible panel so the mobile screen shows only the essentials — map, mic activity and
   * the event feed. Collapsed by default; the user can expand it when needed.
   */
  const showTools = ref(false);

  /** Show/hide the map (persisted). Kept in the DOM with v-show so Leaflet stays alive when toggled. */
  const MAP_KEY = 'pushka:showMap';
  const showMap = ref(loadMapPref());
  function loadMapPref(): boolean {
    try {
      return localStorage.getItem(MAP_KEY) !== '0';
    } catch {
      return true;
    }
  }
  function toggleMap(): void {
    showMap.value = !showMap.value;
    try {
      localStorage.setItem(MAP_KEY, showMap.value ? '1' : '0');
    } catch {
      /* ignore */
    }
  }

  const showQuadrants = ref(false);
  function toggleQuadrants(): void {
    showQuadrants.value = !showQuadrants.value;
    if (showQuadrants.value) {
      map.updateQuadrants();
    } else {
      map.updateQuadrants();
    }
  }

  /**
   * Periodic re-pull of the region from the server (persisted). The radar already receives
   * real-time updates over the WebSocket push, so this is a safety net: it recovers events
   * missed during a brief disconnect/reconnect and ages out stale ones. 0 = off (push only).
   */
  const SYNC_INTERVAL_KEY = 'pushka:syncInterval';
  const DEFAULT_SYNC_INTERVAL_SEC = 60;
  const syncIntervalSec = ref<number>(loadSyncInterval());
  function loadSyncInterval(): number {
    try {
      const raw = localStorage.getItem(SYNC_INTERVAL_KEY);
      if (raw !== null) {
        const v = Number(raw);
        if (Number.isFinite(v) && v >= 0) return v;
      }
    } catch {
      /* ignore */
    }
    return DEFAULT_SYNC_INTERVAL_SEC;
  }
  function setSyncInterval(sec: number): void {
    syncIntervalSec.value = sec;
    try {
      localStorage.setItem(SYNC_INTERVAL_KEY, String(sec));
    } catch {
      /* ignore */
    }
    restartSyncPoll();
  }
  function restartSyncPoll(): void {
    if (syncPollInterval) {
      clearInterval(syncPollInterval);
      syncPollInterval = null;
    }
    if (syncIntervalSec.value > 0) {
      syncPollInterval = setInterval(() => {
        const c = store.userCenter;
        if (c) pullRegion(c);
      }, syncIntervalSec.value * 1000);
    }
  }

  /** Only events from the last RECENCY_MS are shown — the radar is a live monitor. */
  const RECENCY_MS = 6 * 3600 * 1000;
  /** localStorage key for the last known user centre (offline cold-start without GPS). */
  const CENTER_KEY = 'pushka:lastCenter';
  /**
   * Fallback centre when there is neither a cached position nor GPS yet. The map must always be
   * created on mount so it is visible and tappable for manual placement — otherwise a first visit
   * with denied/unavailable geolocation leaves the mini-map empty.
   */
  const DEFAULT_CENTER: LatLon = { lat: 50.4501, lon: 30.5234 }; // Kyiv

  let socket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;
  let regionDebounce: ReturnType<typeof setTimeout> | null = null;
  let tickInterval: ReturnType<typeof setInterval> | null = null;
  let syncPollInterval: ReturnType<typeof setInterval> | null = null;
  let tgLocationPoll: ReturnType<typeof setInterval> | null = null;
  let lastPulledCenter: LatLon | null = null;
  /** Throttle отправки координат в лог посетителей (60 c), чтобы не спамить. */
  let visitorLocTimer: ReturnType<typeof setTimeout> | null = null;

  /** Short label for where the centre came from, shown next to the accuracy badge. */
  const locationLabel = computed(() => {
    switch (locationSource.value) {
      case 'telegram':
        return 'Telegram';
      case 'gps':
        return 'GPS';
      case 'manual':
        return 'вручную';
      case 'cache':
        return 'из кэша';
      default:
        return null;
    }
  });

  // ── Geo helpers ─────────────────────────────────────────────────────────────

  function since(): number {
    return Date.now() - RECENCY_MS;
  }

  function loadCachedCenter(): LatLon | null {
    try {
      const raw = localStorage.getItem(CENTER_KEY);
      if (!raw) return null;
      const c = JSON.parse(raw);
      if (typeof c?.lat === 'number' && typeof c?.lon === 'number') return c;
    } catch {
      /* ignore */
    }
    return null;
  }

  let cloudCenterHydrated = false;

  function saveCachedCenter(c: LatLon): void {
    try {
      localStorage.setItem(CENTER_KEY, JSON.stringify(c));
    } catch {
      /* ignore */
    }
    // Mirror to Telegram CloudStorage: the WebView wipes localStorage between
    // sessions, so without this the app forgets the position on every reopen and
    // re-prompts for geolocation even though the source is still 'gps'/'telegram'.
    if (isTelegramCloudStorageAvailable()) {
      void tgCloudSetItem(CENTER_KEY, JSON.stringify(c));
    }
  }

  /**
   * Best-effort restore of the last known centre from Telegram CloudStorage.
   * Returns the restored centre (and primes localStorage with it) or null.
   * Idempotent — only contacts the cloud once per session.
   */
  async function hydrateCachedCenterFromCloud(): Promise<LatLon | null> {
    if (!isTelegramCloudStorageAvailable() || cloudCenterHydrated) return null;
    cloudCenterHydrated = true;
    const raw = await tgCloudGetItem(CENTER_KEY);
    if (!raw) return null;
    try {
      const c = JSON.parse(raw) as LatLon;
      if (typeof c?.lat === 'number' && typeof c?.lon === 'number') {
        try {
          localStorage.setItem(CENTER_KEY, raw);
        } catch {
          /* ignore */
        }
        return c;
      }
    } catch {
      /* ignore malformed payload */
    }
    return null;
  }

  /**
   * Pin the user to a hand-picked point — either tapped on the map or typed into the coordinate
   * fields. A deliberate manual choice wins, so continuous tracking is turned off.
   */
  function setManualLocation(lat: number, lon: number): void {
    const loc: Location = { latitude: lat, longitude: lon, accuracy: MANUAL_ACCURACY_M };
    lastLocation.value = loc;
    accuracy.value = MANUAL_ACCURACY_M;
    locationSource.value = 'manual';
    saveLocationSource('manual');
    manualMode.value = false;
    // Mobile: restore the settings panel if manual placement collapsed it — the panel then surfaces
    // the "✓ Координаты применены" confirmation (driven by manualApplied below).
    if (toolsBeforePick) {
      showTools.value = true;
      toolsBeforePick = false;
    }
    gpsError.value = null;
    if (liveTracking.value) {
      liveTracking.value = false;
      saveTrackingPref(false);
    }
    stopWatching();

    const center: LatLon = { lat, lon };
    manualLat.value = lat.toFixed(5);
    manualLon.value = lon.toFixed(5);
    // Keep the current zoom — the user already zoomed in to pick the spot; don't yank it around.
    applyCenter(center, true, true);
    pullRegion(center);

    // Confirm visibly that the point was committed — the input often holds the current centre, so
    // tapping OK could otherwise look like nothing happened.
    manualApplied.value = true;
    if (manualAppliedTimer) clearTimeout(manualAppliedTimer);
    manualAppliedTimer = setTimeout(() => {
      manualApplied.value = false;
    }, 2200);
  }

  // ── Region pull + persistence ───────────────────────────────────────────────

  function persistCache(): void {
    cache.saveExplosions(store.explosions).catch(() => {});
    cache.saveTrajectories(store.trajectories).catch(() => {});
  }

  function pullRegion(center: LatLon): void {
    const bbox = bboxAround(center, NEARBY_RADIUS_M);
    socket?.emit('subscribe-region', bbox);
    store.fetchByRegion(bbox, since()).then(() => {
      map.updateMarkers();
      map.updateTrajectories();
      map.updateTracks();
      persistCache();
    });
    // Keep background push following the user's region of interest.
    void updatePushRegion(center).catch(() => {});
    lastPulledCenter = center;
  }

  // ── Online-monitor presence ─────────────────────────────────────────────────
  // Tell the server we're an online monitor so it can count how many people are
  // listening in the same area — and so neighbours' counters go up when we join.

  /** Register as an online monitor at the current centre. No-op without coordinates. */
  function emitStartMonitor(): void {
    const c = store.userCenter;
    if (socket && c) {
      // deviceId связывает подключение мониторинга с detection-событиями
      // (Telegram-уведомление «кто включил мониторинг»). Отправляем после
      // резолва хэша — задержка в единицы мс некритична.
      const s = socket;
      void getDeviceId().then((deviceId) => {
        s.emit('start-monitor', { lat: c.lat, lon: c.lon, deviceId });
      });
    }
    // Лог посетителей: радар вкл + последняя координата.
    if (c) visitorLog.monitorStart({ ...c, accuracy: accuracy.value ?? undefined });
  }

  /** Go offline as a monitor and clear the local "nearby" count. */
  function emitStopMonitor(): void {
    socket?.emit('stop-monitor');
    store.setMonitorsNearby(0);
    visitorLog.monitorStop();
  }

  // ── GPS ─────────────────────────────────────────────────────────────────────

  function applyCenter(center: LatLon, fromGps: boolean, keepZoom = false): void {
    trace.log('[LiveRadar][applyCenter]', { center, fromGps, keepZoom });
    store.setUserCenter(center);
    if (fromGps) saveCachedCenter(center);

    // Создаёт карту при первом центре, дальше recenter + обновление маркеров.
    map.setCenter(center, keepZoom);

    // If we're already monitoring, (re)register presence at the new point: this covers both the
    // first fix arriving after monitoring was turned on, and moving to a new location.
    if (detection.monitoring.value) emitStartMonitor();

    // Лог посетителей: запоминаем последнюю координату (throttle 60 c), чтобы у
    // записей был meaningful location даже когда радар не включали.
    if (fromGps && !visitorLocTimer) {
      visitorLog.location({ ...center, accuracy: accuracy.value ?? undefined });
      visitorLocTimer = setTimeout(() => {
        visitorLocTimer = null;
      }, 60_000);
    }
  }

  function onGpsUpdate(center: LatLon): void {
    const moved =
      !lastPulledCenter ||
      Math.abs(center.lat - lastPulledCenter.lat) > 0.0001 ||
      Math.abs(center.lon - lastPulledCenter.lon) > 0.0001;

    applyCenter(center, true);

    // Only re-pull the region when the user has meaningfully moved.
    if (moved) {
      if (regionDebounce) clearTimeout(regionDebounce);
      regionDebounce = setTimeout(() => pullRegion(center), 400);
    }
  }

  function useLocation(loc: Location, source: LocationSource): LatLon {
    lastLocation.value = loc;
    accuracy.value = loc.accuracy;
    locationSource.value = source;
    saveLocationSource(source);
    return { lat: loc.latitude, lon: loc.longitude };
  }

  /**
   * Whether the user's position is already known — from a live fix this session,
   * a cached/cloud centre, or a non-manual source. Used to gate geolocation
   * requests: we only prompt for geolocation when the position is genuinely
   * unknown ("not set at all"), never as a side effect of opening the app or
   * arming the mic when a position is already available.
   */
  function isLocationKnown(): boolean {
    if (lastLocation.value !== null) return true;
    const src = locationSource.value;
    return !!src && src !== 'manual' && loadCachedCenter() !== null;
  }

  async function acquireLocation(): Promise<LatLon | null> {
    // Inside Telegram, prefer the Mini App's native LocationManager.
    if (telegramLocation) {
      try {
        const tgLoc = await getTelegramLocation();
        if (tgLoc) return useLocation(tgLoc, 'telegram');
      } catch {
        /* fall through to the browser geolocation */
      }
    }
    try {
      const loc = await gps.requestLocation();
      return useLocation(loc, 'gps');
    } catch (e) {
      gpsError.value = (e as Error).message;
      return null;
    }
  }

  /**
   * Re-read the position once (Telegram / GPS). This is a single shot — GPS is not kept on unless
   * the user has enabled live tracking — so it's cheap on the battery.
   */
  async function retryLocation(): Promise<void> {
    manualMode.value = false;
    gpsError.value = null;
    locating.value = true;
    const center = await acquireLocation();
    locating.value = false;
    if (center) {
      applyCenter(center, true);
      pullRegion(center);
      if (liveTracking.value) startWatching();
    }
  }

  /**
   * Locate the device exactly once ("сейчас где я"). Turns off continuous tracking if it was on,
   * so the position stays fixed afterwards to save the battery.  If the position was already
   * acquired (gps/telegram) and not manual, skip the GPS request entirely.
   */
  async function locateOnce(): Promise<void> {
    if (liveTracking.value) {
      liveTracking.value = false;
      saveTrackingPref(false);
      stopWatching();
    }
    if (
      lastLocation.value &&
      locationSource.value !== null &&
      locationSource.value !== 'manual'
    ) {
      return;
    }
    await retryLocation();
  }

  /** Toggle continuous location tracking (battery-hungry; off by default). */
  async function toggleLiveTracking(): Promise<void> {
    liveTracking.value = !liveTracking.value;
    saveTrackingPref(liveTracking.value);
    if (liveTracking.value) {
      // Refresh once, then keep following.
      const center = await acquireLocation();
      if (center) {
        applyCenter(center, true);
        pullRegion(center);
      }
      startWatching();
    } else {
      stopWatching();
    }
  }

  function startWatching(): void {
    stopWatching();
    if (telegramLocation) {
      // LocationManager has no watch API — poll it on an interval instead.
      tgLocationPoll = setInterval(async () => {
        const loc = await getTelegramLocation();
        if (loc) onGpsUpdate(useLocation(loc, 'telegram'));
      }, 20000);
      return;
    }
    gps.startWatching((loc) => {
      onGpsUpdate(useLocation(loc, 'gps'));
    });
  }

  function stopWatching(): void {
    gps.stopWatching();
    if (tgLocationPoll) {
      clearInterval(tgLocationPoll);
      tgLocationPoll = null;
    }
  }

  /** Mobile viewport (matches the page's <900px responsive breakpoint). Guarded for jsdom/SSR. */
  function isMobileViewport(): boolean {
    return (
      typeof window !== 'undefined' &&
      !!window.matchMedia &&
      window.matchMedia('(max-width: 899px)').matches
    );
  }

  /** Arm/disarm manual placement (tap the map or type coordinates). */
  function toggleManualMode(): void {
    manualMode.value = !manualMode.value;
    if (manualMode.value) {
      manualError.value = null;
      // Mobile: collapse the settings panel so the map fills the screen for precise tapping.
      // Remember its prior state so setManualLocation can restore it after the point is committed.
      if (isMobileViewport()) {
        toolsBeforePick = showTools.value;
        showTools.value = false;
      }
      // Prefill the coordinate fields with the current centre as a convenient starting point.
      const c =
        store.userCenter ||
        (lastLocation.value
          ? { lat: lastLocation.value.latitude, lon: lastLocation.value.longitude }
          : null);
      if (c) {
        manualLat.value = c.lat.toFixed(5);
        manualLon.value = c.lon.toFixed(5);
      }
      // Zoom in so the point can be placed precisely (never zooms out).
      map.zoomForPick(c);
    } else {
      // Cancelled without committing — drop the remembered state so we don't auto-reopen later.
      toolsBeforePick = false;
    }
  }

  /** Apply the hand-typed latitude/longitude after validating the ranges. */
  function applyManualCoords(): void {
    const lat = parseFloat(manualLat.value.replace(',', '.'));
    const lon = parseFloat(manualLon.value.replace(',', '.'));
    if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
      manualError.value = 'Широта должна быть в диапазоне −90…90';
      return;
    }
    if (!Number.isFinite(lon) || lon < -180 || lon > 180) {
      manualError.value = 'Долгота должна быть в диапазоне −180…180';
      return;
    }
    manualError.value = null;
    setManualLocation(lat, lon);
  }

  // ── WebSocket ───────────────────────────────────────────────────────────────

  function connectWebSocket(): void {
    socket = io(env.wsUrl, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
    }) as Socket<ServerToClientEvents, ClientToServerEvents>;

    socket.on('connect', () => {
      store.setConnected(true);
      store.clearError();
      if (store.userCenter) {
        socket?.emit('subscribe-region', bboxAround(store.userCenter, NEARBY_RADIUS_M));
      }
      // Re-register presence after a reconnect so the count stays accurate.
      if (detection.monitoring.value) emitStartMonitor();
      // Alert pushes missed while disconnected aren't replayed — re-fetch the snapshot.
      void alertStore.fetchSnapshot();
      if (store.lastDisconnectTime) {
        store.syncGap().then(() => {
          map.updateMarkers();
          persistCache();
        });
      }
    });

    socket.on('disconnect', () => {
      store.setConnected(false);
      store.markDisconnected();
      store.setMonitorsNearby(0);
      store.setMonitorQuadrants([]);
    });

    socket.on('monitors-count', (payload) => {
      store.setMonitorsNearby(payload.count);
      const quads = payload.quadrants ?? [];
      store.setMonitorQuadrants(quads);
      // Verbose-only: building quadrantDetails + JSON.stringify on every monitors-count message
      // is pure overhead unless a developer is actually inspecting the trace.
      trace.lazy(() => {
        const QUADRANT_LAT_DELTA = 2000 / 111320;
        const QUADRANT_LON_DELTA = QUADRANT_LAT_DELTA * 2;
        return [
          '[LiveRadar][monitors-count]',
          {
            count: payload.count,
            quadrants: quads.length,
            showQuadrants: showQuadrants.value,
            quadrantDetails: quads.map((q) => ({
              row: q.row,
              col: q.col,
              count: q.count,
              lat: +(q.row * QUADRANT_LAT_DELTA).toFixed(5),
              lon: +(q.col * QUADRANT_LON_DELTA).toFixed(5),
            })),
          },
        ];
      });
      if (showQuadrants.value) map.updateQuadrants();
    });

    socket.on('quadrant-alert', (payload) => {
      store.addExplosionQuadrant(payload);
      map.highlightExplosionQuadrant(payload.row, payload.col, payload.count);
      trace.log('[LiveRadar][quadrant-alert]', payload);
    });

    socket.on('cluster-update', (payload) => {
      store.handleClusterUpdate(payload);
      map.updateMarkers();
      persistCache();
    });

    socket.on('trajectory-update', (payload) => {
      store.handleTrajectoryUpdate(payload);
      map.updateTrajectories();
      persistCache();
    });

    socket.on('track-update', (payload) => {
      store.handleTrackUpdate(payload);
      map.updateTracks();
    });

    socket.on('alerts-update', (payload) => {
      alertStore.applyUpdate(payload);
    });
  }

  async function manualSync(): Promise<void> {
    syncing.value = true;
    try {
      if (store.userCenter) {
        pullRegion(store.userCenter);
      }
      await store.syncGap();
      map.updateMarkers();
      persistCache();
    } finally {
      syncing.value = false;
    }
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────────

  onMounted(async () => {
    // Idempotent: already installed by live-main.ts; this covers the page when it is
    // mounted from another entry (e.g. the admin app's router).
    installGlobalErrorCapture();

    trace.log('[LiveRadar][onMounted] START', {
      showMap: showMap.value,
      wsUrl: env.wsUrl,
      telegramLocation,
    });

    // 1) Seed centre + render the map FIRST so it is visible immediately. This must not wait on the
    //    cache: a blocked/slow IndexedDB upgrade would otherwise leave the screen blank.
    let cachedCenter = loadCachedCenter();
    const savedSource = loadLocationSource();
    trace.log('[LiveRadar][onMounted] cachedCenter', cachedCenter, 'savedSource', savedSource);

    // Hydrate location source from Telegram CloudStorage (async, best-effort).
    void hydrateLocationSourceFromCloud();

    if (cachedCenter) {
      locationSource.value = savedSource ?? 'cache';
      saveLocationSource(locationSource.value);
      applyCenter(cachedCenter, false);
      // Restore the detection-side location so events emitted right after reload already
      // carry coordinates — without this, detections fire before a fresh GPS/Telegram fix
      // and get marked "no_location" even though the map shows the cached point. The cache
      // stores only {lat,lon}, so use the manual accuracy as a conservative floor (a cached
      // point is at least block-level precise; a live fix will overwrite this on arrival).
      lastLocation.value = {
        latitude: cachedCenter.lat,
        longitude: cachedCenter.lon,
        accuracy: MANUAL_ACCURACY_M,
      };
      accuracy.value = MANUAL_ACCURACY_M;
    } else {
      // localStorage doesn't survive a Telegram WebView session — render the
      // default centre immediately so the map is visible, then try restoring the
      // last known centre from Telegram CloudStorage before any GPS prompt.
      map.init(DEFAULT_CENTER, false);
      const cloudCenter = await hydrateCachedCenterFromCloud();
      if (cloudCenter) {
        cachedCenter = cloudCenter;
        locationSource.value = savedSource ?? 'cache';
        saveLocationSource(locationSource.value);
        applyCenter(cloudCenter, false);
        lastLocation.value = {
          latitude: cloudCenter.lat,
          longitude: cloudCenter.lon,
          accuracy: MANUAL_ACCURACY_M,
        };
        accuracy.value = MANUAL_ACCURACY_M;
        trace.log('[LiveRadar][onMounted] centre restored from Telegram CloudStorage', cloudCenter);
      } else {
        trace.log('[LiveRadar][onMounted] no cached centre (local or cloud)');
      }
    }

    // 2) Hydrate from the offline cache (best-effort). Bounded by a timeout inside cache.open() so a
    //    stale/blocked DB can never freeze the app; on failure the map simply stays empty.
    try {
      await cache.open();
      const [cachedExp, cachedTraj] = await Promise.all([
        cache.getCachedExplosions(),
        cache.getCachedTrajectories(),
      ]);
      store.hydrateFromCache(cachedExp, cachedTraj);
      map.updateMarkers();
      map.updateTrajectories();
      map.updateTracks();
      trace.log('[LiveRadar][onMounted] cache hydrated', {
        explosions: cachedExp.length,
        trajectories: cachedTraj.length,
      });
    } catch (e) {
      trace.warn('[LiveRadar][onMounted] cache hydrate failed (best-effort)', e);
    }

    // 2b) Load the device's own past detections from IndexedDB so the "Мои события" tab is
    //     populated on open (the live-radar entry doesn't init this store like the admin app).
    void detectionStore.init().catch((e) => {
      trace.warn('[LiveRadar][onMounted] detection store init failed (best-effort)', e);
    });

    // 3) Connect realtime + acquire live GPS.
    connectWebSocket();

    // Decide whether GPS is needed based on the persisted location source:
    // - manual: never request GPS, user places themselves on the map.
    // - gps/telegram/cache with cached centre: position already known, skip GPS.
    // - null (first visit) or no cached centre: request GPS.
    const effectiveSource = locationSource.value;
    if (effectiveSource === 'manual') {
      trace.log('[LiveRadar][onMounted] manual mode → skip GPS');
      locating.value = false;
      if (cachedCenter) pullRegion(cachedCenter);
    } else if (effectiveSource && cachedCenter) {
      trace.log('[LiveRadar][onMounted] source=', effectiveSource, '→ reuse cached centre');
      locating.value = false;
      pullRegion(cachedCenter);
      if (liveTracking.value) {
        const freshCenter = await acquireLocation();
        locating.value = false;
        if (freshCenter) {
          applyCenter(freshCenter, true);
          pullRegion(freshCenter);
          startWatching();
        }
      }
    } else if (isLocationKnown()) {
      // Position already known some other way (e.g. a live fix this session) —
      // never prompt for geolocation as a side effect of opening the app or
      // arming the mic. Just pull the region for the known centre.
      trace.log('[LiveRadar][onMounted] location already known → skip GPS prompt');
      locating.value = false;
      const known = store.userCenter;
      if (known) pullRegion(known);
    } else {
      const center = await acquireLocation();
      trace.log('[LiveRadar][onMounted] acquireLocation →', center);
      locating.value = false;
      if (center) {
        applyCenter(center, true);
        pullRegion(center);
        if (liveTracking.value) startWatching();
      } else if (cachedCenter) {
        pullRegion(cachedCenter);
      }
    }

    // 4) Slide the recency window / refresh relative times every 30s.
    tickInterval = setInterval(() => {
      map.updateMarkers();
      map.updateTrajectories();
      map.updateTracks();
    }, 30000);

    // 4b) Periodic server re-pull (safety net over the WebSocket push), if enabled.
    restartSyncPoll();

    // 5) Air-raid alert status: hydrate the snapshot and keep the staleness clock running,
    //    then resume monitoring if it was armed before the reload. In alertOnly mode the
    //    reconciler keeps the detector dormant until an alert actually starts. Auto-start
    //    requires an already-granted mic permission; if a user gesture is still required,
    //    start() throws and the reconciler disarms so the UI stays consistent.
    alertStore.startTicker();
    await alertStore.fetchSnapshot();
    await reconcileMonitoring();

    trace.log('[LiveRadar][onMounted] END');

    // Лог посетителей: создаём сессию по IP (best-effort, не блокирует UI).
    // deviceId/userId те же, что у детекций — SHA-256 хэш Telegram id или dev-uuid.
    void Promise.all([getDeviceId(), getUserId()]).then(([deviceId, userId]) => {
      void visitorLog.start({ deviceId, userId });
    });
  });

  onUnmounted(() => {
    emitStopMonitor();
    detection.dispose();
    socket?.disconnect();
    stopWatching();
    alertStore.stopTicker();
    if (regionDebounce) clearTimeout(regionDebounce);
    if (tickInterval) clearInterval(tickInterval);
    if (syncPollInterval) clearInterval(syncPollInterval);
    if (manualAppliedTimer) clearTimeout(manualAppliedTimer);
    if (visitorLocTimer) clearTimeout(visitorLocTimer);
    // Лог посетителей: фиксируем выход. sendBeacon + keepalive fetch.
    visitorLog.leave();
  });

  return {
    // состояние
    detection,
    locating,
    gpsError,
    syncing,
    accuracy,
    locationLabel,
    locationSource,
    manualMode,
    manualLat,
    manualLon,
    manualError,
    manualApplied,
    liveTracking,
    waitingForAlert,
    showMap,
    showQuadrants,
    showTools,
    syncIntervalSec,
    // действия
    toggleMonitoring,
    manualSync,
    setSyncInterval,
    toggleMap,
    toggleQuadrants,
    setManualLocation,
    retryLocation,
    toggleLiveTracking,
    locateOnce,
    toggleManualMode,
    applyManualCoords,
  };
}
